/**
 * AppLayout Component
 * Main authenticated application layout with chat, tools, and settings
 * This is the protected content shown after user login
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Sidebar from './Sidebar';
import ChatArea from './ChatArea';
import { MentionItem } from './ChatInput';
import RagToolsPanel from './RagToolsPanel';
import LegalDrafter from './LegalDrafter';
import LibraryPage from './LibraryPage';
import { isOnboardingCompleted, resetOnboarding } from './OnboardingGuide';
import { PreviewFile } from './FilePreviewModal';
import CreatorStudio from './workflow/CreatorStudio';
import GlobalModalSlot from './slots/GlobalModalSlot';

import {
    FileNode,
    openFolder,
    scanDirectory,
    buildFileTreeListing,
    restoreDirectoryHandle,
    isFileSystemSupported,
} from '../hooks/useFileSystem';

import { CaseFolder } from '../types/library';
import { API_BASE_URL } from '../services/api';

import { useAuth } from '../contexts/AuthContext';
import { ChatSSEService, ChatApiService, KnowledgeApiService, ToolHistoryApiService, ToolHistoryListItem, ToolType, ResourceApiService, FileApiService } from '../services/api';
import { WorkflowApiService, InstalledSkill } from '../services/workflowApi';
import { FileUploadState } from './ChatInput';
import { Message, Session, RagTool, ThinkingMode } from '../types';
import { useTranslation } from '../hooks/useTranslation';

// Architecture 3.0 Phase 4: 前端工具调度器
import { dispatchFrontendTool, type FrontendToolContext } from '../tools/frontend-dispatcher';
import '../tools/handlers'; // 自动注册所有前端工具处理器

// Architecture 3.0 Phase 4: 状态钩子抽离
import { useCloudStorage } from '../hooks/useCloudStorage';
import { useEmailProviders } from '../hooks/useEmailProviders';
import { useAiIndexWorker } from '../hooks/useAiIndexWorker';

// Architecture 3.0 Phase 4: Zustand 全局黑板
import { useAgentOSStore } from '../store/AgentOSStore';

// 归档会话类型
interface ArchivedSession {
    id: string;
    title: string;
    createdAt: string;
    updatedAt: string;
}

interface AppLayoutProps {
    currentLanguage: string;
    onLanguageChange: (lang: string) => void;
}

// ─── File type classification for attachment routing ───
// Media files → native Gemini multimodal (no text extraction)
// Document files → officeparser text extraction → documentContext
const NATIVE_MEDIA_EXTENSIONS = new Set([
    // Images
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif',
    // Audio
    'mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma',
    // Video
    'mp4', 'webm', 'mov', 'avi', 'mkv', 'wmv', 'flv',
]);

function isNativeMediaFile(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return NATIVE_MEDIA_EXTENSIONS.has(ext);
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            // Strip the data:mime;base64, prefix
            const base64 = dataUrl.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const AppLayout: React.FC<AppLayoutProps> = ({ currentLanguage, onLanguageChange }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // 应用状态
    const [sessions, setSessions] = useState<Session[]>([]);
    const [archivedSessions, setArchivedSessions] = useState<ArchivedSession[]>([]);
    const [toolHistoryRecords, setToolHistoryRecords] = useState<ToolHistoryListItem[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const currentSessionIdRef = useRef<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [ragTools, setRagTools] = useState<RagTool[]>([]);

    // Architecture 3.0 Phase 4: UI 状态由 Zustand Store 管理
    const {
        isRagPanelVisible, toggleRagPanel, setRagPanelVisible,
        openLanguageSelector, openPricing, openOnboarding,
        isSettingsOpen, openSettings,
        previewFile, setPreviewFile,
        knowledgeUploadState, setKnowledgeUploadState,
    } = useAgentOSStore();

    // 🧩 Architecture 3.0: 已安装的 SubAgent 技能
    const [installedSkills, setInstalledSkills] = useState<InstalledSkill[]>([]);

    // 本地文件系统根目录 Handle (用于给 Agent 读取文件)
    const [localDirHandle, setLocalDirHandle] = useState<any>(null);

    // Architecture 3.0 Phase 4: 状态钩子
    const { cloudProviders, cloudFiles, handleCloudConnect, handleCloudDisconnect, loadCloudFiles, handleCloudFolderClick, handleCloudFolderBack, initCloudProviders } = useCloudStorage();
    const { emailProviders, emailMessages, readingEmail, setReadingEmail, handleEmailConnect, handleEmailDisconnect, loadEmailMessages, loadMoreEmails, handleEmailRightClick, initEmailProviders } = useEmailProviders();
    const { aiIndex, isIndexing, indexingProgress, localFileTreeListing, setLocalFileTreeListing } = useAiIndexWorker(localDirHandle);

    // 文档审查/起草模式状态
    const [initialDraftSessionId, setInitialDraftSessionId] = useState<string | null>(null);
    const [forceNewDraft, setForceNewDraft] = useState(0);

    // 知识库搜索开关状态
    const [isKnowledgeSearchEnabled, setIsKnowledgeSearchEnabled] = useState(true);

    // 案件资料库状态
    const [caseFolders, setCaseFolders] = useState<CaseFolder[]>([]);

    // 文件上下文状态
    const [uploadedFileContext, setUploadedFileContext] = useState<{
        extractedText: string;
        filename: string;
    } | null>(null);

    // 文件上传进度状态
    const [fileUploadState, setFileUploadState] = useState<FileUploadState | null>(null);

    // knowledgeUploadState 已由 AgentOSStore 管理

    // 知识库加载状态
    const [isRagToolsLoading, setIsRagToolsLoading] = useState(false);

    // readingEmail 已由 useEmailProviders 管理
    // previewFile 已由 AgentOSStore 管理

    // 聊天输入框状态持久化 - 切换页面时保留输入内容
    const [chatInputMessage, setChatInputMessage] = useState('');

    // 构建 @ 提及文件列表（从案件文件夹 + 本地文件树）
    const mentionFiles: MentionItem[] = useMemo(() => {
        const items: MentionItem[] = [];

        // 从案件文件夹提取文件（只包含已启用的文件夹）
        for (const folder of caseFolders.filter(f => f.enabled)) {
            for (const file of folder.files) {
                items.push({
                    id: file.id,
                    name: file.name,
                    source: 'case',
                    folderName: folder.name,
                });
            }
        }

        // 从本地文件树字符串提取文件名
        if (localFileTreeListing) {
            const lines = localFileTreeListing.split('\n');
            for (const line of lines) {
                const trimmed = line.trim();
                // 跳过文件夹行和智能标签行
                if (trimmed.startsWith('📁') || trimmed.startsWith('↳') || !trimmed) continue;
                // 提取文件名（跳过 emoji 前缀：emoji + 空格 + 文件名）
                const spaceIdx = trimmed.indexOf(' ');
                if (spaceIdx > 0 && spaceIdx < 5) {
                    const fileName = trimmed.slice(spaceIdx + 1).trim();
                    if (fileName) {
                        items.push({
                            id: `local-${fileName}`,
                            name: fileName,
                            source: 'local',
                            folderName: 'Local',
                        });
                    }
                }
            }
        }

        // 从云存储文件列表提取 (文件夹 + 文件)
        for (const cf of cloudFiles) {
            const sourceLabel = cf.source === 'gdrive' ? 'GDrive'
                : cf.source === 'onedrive' ? 'OneDrive'
                : cf.source === 'slack' ? 'Slack' : cf.source;
            for (const file of cf.files) {
                items.push({
                    id: file.id,
                    name: file.isFolder ? `📁 ${file.name}` : file.name,
                    source: cf.source as any,
                    folderName: file.isFolder ? sourceLabel : `${sourceLabel}/${file.parentName}`,
                });
            }
        }

        // 从邮件列表提取 (可 @ 引用邮件)
        for (const em of emailMessages) {
            const sourceLabel = em.source === 'outlook' ? 'Outlook' : 'Gmail';
            for (const email of em.emails.slice(0, 15)) {
                const shortDate = email.date ? new Date(email.date).toLocaleDateString() : '';
                items.push({
                    id: email.id,
                    name: `📧 ${email.subject}`,
                    source: em.source as any,
                    folderName: `${sourceLabel}/${email.from}${shortDate ? ` · ${shortDate}` : ''}`,
                });
            }
        }

        return items;
    }, [caseFolders, localFileTreeListing, cloudFiles, emailMessages]);

    // Auth hook
    const { user, logout } = useAuth();

    // Check if user has paid subscription (Pro, Max, Ultra, Enterprise)
    const isPaidUser = (): boolean => {
        const tier = user?.tier || 'free';
        return ['pro', 'max', 'ultra', 'enterprise'].includes(tier);
    };

    // Show upgrade prompt for Free users
    const showUpgradePrompt = (feature: string) => {
        const isZh = currentLanguage.startsWith('zh');
        const message = isZh
            ? `${feature}功能需要 Pro 或更高级别订阅。是否查看升级选项？`
            : `${feature} requires a Pro or higher subscription. View upgrade options?`;

        if (window.confirm(message)) {
            openPricing();
        }
    };

    // SSE服务引用
    const sseServiceRef = useRef<ChatSSEService>(new ChatSSEService());

    // 取消请求控制器
    const abortControllerRef = useRef<AbortController | null>(null);

    // 翻译hook
    const { t } = useTranslation(currentLanguage);

    // Check if we're on a specific tool route
    const isOnDraftRoute = location.pathname === '/app/draft';
    const isOnSettingsRoute = location.pathname === '/app/settings';
    const isOnKnowledgeRoute = location.pathname === '/app/knowledge';
    const isOnLibraryRoute = location.pathname === '/app/library';
    const isOnStudioRoute = location.pathname === '/app/studio'; // 🧩 Architecture 3.0

    // Keep sessionId ref in sync (avoids stale closure in onToolRequest callbacks)
    useEffect(() => {
        currentSessionIdRef.current = currentSessionId;
    }, [currentSessionId]);

    // Route-level protection: redirect Free users from AI tools
    useEffect(() => {
        if (isOnDraftRoute && !isPaidUser()) {
            showUpgradePrompt(currentLanguage.startsWith('zh') ? 'AI 起草' : 'AI Draft');
            navigate('/app', { replace: true });
        }
    }, [isOnDraftRoute, user?.tier]);

    // AI 文件索引已由 useAiIndexWorker 管理

    // 初始化应用 - 仅清理资源，实际加载会话在 user?.id 变化时触发
    useEffect(() => {
        // 不再调用 initializeApp()，因为此时 user?.id 可能尚未可用
        // 会话加载逻辑已在 user?.id 变化的 useEffect 中处理 (第157-169行)

        return () => {
            sseServiceRef.current.disconnect();
        };
    }, []);

    // 首次访问时显示引导
    useEffect(() => {
        if (!isOnboardingCompleted()) {
            openOnboarding();
        }
    }, []);

    // 处理支付成功回调
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('payment') === 'success') {
            alert('Payment Successful! Plan updated automatically.');
            // Clean up URL params
            navigate('/app', { replace: true });
        }
    }, [location.search, navigate]);

    // 用户信息变化时重新初始化
    useEffect(() => {
        const guestId = localStorage.getItem('guest_user_id');
        if (user?.id) {
            initializeRagTools(user.id);
            ChatApiService.getSessions(user.id)
                .then(response => setSessions(response.sessions || []))
                .catch(err => console.error('Failed to fetch sessions:', err));
            fetchArchivedSessions();
            fetchToolHistory();
            // 加载案件资料库
            ResourceApiService.getFolders()
                .then(response => {
                    if (response.success && response.folders) {
                        setCaseFolders(response.folders.map(f => ({
                            id: f.id,
                            name: f.name,
                            status: f.status as any,
                            enabled: f.enabled,
                            createdAt: new Date(f.createdAt),
                            updatedAt: new Date(f.updatedAt),
                            files: (f.files || []).map((file: any) => ({
                                id: file.id,
                                name: file.name,
                                type: file.type,
                                category: file.category,
                                size: file.size,
                                uploadedAt: new Date(file.uploadedAt),
                            })),
                        })));
                    }
                })
                .catch(err => console.error('Failed to fetch case folders:', err));
            // 🧩 Architecture 3.0: 加载已安装的 SubAgent 技能
            WorkflowApiService.getInstalled()
                .then(response => {
                    if (response.items) {
                        setInstalledSkills(response.items);
                        console.log(`[AppLayout] 🧩 Loaded ${response.items.length} installed skills`);
                    }
                })
                .catch(err => console.warn('[AppLayout] 🧩 Skills load failed (may not have workflow tables):', err));
            // Architecture 3.0 Phase 4: 通过 hooks 初始化云存储和邮件
            initCloudProviders();
            initEmailProviders();
        } else if (guestId) {
            initializeRagTools(guestId);
        }
    }, [user?.id]);

    // Auto-open tools based on route
    useEffect(() => {
        if (isOnSettingsRoute) {
            openSettings();
        }
    }, [isOnSettingsRoute]);

    // 初始化应用数据
    const initializeApp = async () => {
        try {
            const response = await ChatApiService.getSessions(user?.id);
            setSessions(response.sessions || []);
            fetchArchivedSessions();
            fetchToolHistory();
        } catch (error) {
            console.error('获取会话列表失败:', error);
            setSessions([]);
        }
    };

    // 获取归档会话列表
    const fetchArchivedSessions = async () => {
        try {
            const archived = await ChatApiService.getArchivedSessions(user?.id);
            setArchivedSessions(archived || []);
        } catch (error) {
            console.error('获取归档会话失败:', error);
            setArchivedSessions([]);
        }
    };

    // 获取工具历史记录
    const fetchToolHistory = async () => {
        try {
            const response = await ToolHistoryApiService.getRecords(undefined, user?.id);
            setToolHistoryRecords(response.records || []);
        } catch (error) {
            console.error('获取工具历史失败:', error);
            setToolHistoryRecords([]);
        }
    };

    // 处理工具历史记录选择
    const handleToolHistorySelect = (record: ToolHistoryListItem) => {
        // Check tier permission for AI tools
        if (!isPaidUser()) {
            const toolNames: Record<string, string> = {
                review: currentLanguage.startsWith('zh') ? 'AI 审查' : 'AI Review',
                draft: currentLanguage.startsWith('zh') ? 'AI 起草' : 'AI Draft',
            };
            showUpgradePrompt(toolNames[record.toolType] || 'AI Tool');
            return;
        }

        switch (record.toolType) {
            case 'review':
                navigate('/app/review');
                break;
            case 'draft':
                setInitialDraftSessionId(record.id);
                navigate('/app/draft');
                break;
        }
    };

    // 处理工具历史记录删除
    const handleToolHistoryDelete = async (recordId: string) => {
        try {
            await ToolHistoryApiService.deleteRecord(recordId, user?.id);
            setToolHistoryRecords(prev => prev.filter(r => r.id !== recordId));
        } catch (error) {
            console.error('删除工具历史失败:', error);
        }
    };

    // 创建工具历史保存回调
    const createToolHistorySave = (toolType: ToolType) => {
        return async (data: { title: string; summary: string; inputData?: object; outputData?: object }) => {
            try {
                await ToolHistoryApiService.createRecord({
                    toolType,
                    title: data.title,
                    summary: data.summary,
                    inputData: data.inputData as Record<string, unknown> | undefined,
                    outputData: data.outputData as Record<string, unknown> | undefined,
                }, user?.id);
                fetchToolHistory();
            } catch (error) {
                console.error('保存工具历史失败:', error);
            }
        };
    };

    // 根据文件扩展名获取图标
    const getFileIcon = (filename: string): string => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        if (ext === 'pdf') return '📄';
        if (['doc', 'docx'].includes(ext)) return '📝';
        if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return '🖼️';
        if (['txt', 'md'].includes(ext)) return '📄';
        return '📁';
    };

    // 根据文件扩展名获取类型
    const getFileType = (filename: string): 'pdf' | 'doc' | 'png' | 'jpg' | 'txt' | 'other' => {
        const ext = filename.split('.').pop()?.toLowerCase() || '';
        if (ext === 'pdf') return 'pdf';
        if (['doc', 'docx'].includes(ext)) return 'doc';
        if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) return 'png';
        if (['txt', 'md'].includes(ext)) return 'txt';
        return 'other';
    };

    // 初始化用户知识库文档
    const initializeRagTools = async (targetUserId?: string) => {
        const userId = targetUserId || user?.id || localStorage.getItem('guest_user_id') || undefined;
        setIsRagToolsLoading(true);
        try {
            const response = await KnowledgeApiService.getDocuments(userId);
            const tools: RagTool[] = (response.documents || []).map(doc => ({
                id: doc.id,
                name: doc.name,
                type: getFileType(doc.name),
                icon: getFileIcon(doc.name),
            }));
            setRagTools(tools);
        } catch (error) {
            console.error('获取知识库文档失败:', error);
            setRagTools([]);
        } finally {
            setIsRagToolsLoading(false);
        }
    };

    // 选择会话
    const handleSessionSelect = async (sessionId: string) => {
        if (sessionId === currentSessionId) return;
        setCurrentSessionId(sessionId);
        setMessages([]);
        setIsLoading(true);
        try {
            sseServiceRef.current.disconnect();
            const historyData = await ChatApiService.getHistory(sessionId);
            setMessages(historyData.messages);
        } catch (error) {
            console.error('切换会话失败:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 创建新会话
    const handleNewSession = async (): Promise<string> => {
        try {
            const response = await ChatApiService.createSession(user?.id);
            const newSession: Session = {
                session_id: response.session_id,
                title: 'New Chat',
                created_at: Date.now(),
                updated_at: Date.now(),
            };
            setSessions(prev => [newSession, ...prev]);
            setCurrentSessionId(newSession.session_id);
            setMessages([]);
            return newSession.session_id;
        } catch (error) {
            console.error('创建新会话失败:', error);
            const newSessionId = uuidv4();
            const newSession: Session = {
                session_id: newSessionId,
                title: 'New Chat',
                created_at: Date.now(),
                updated_at: Date.now(),
            };
            setSessions(prev => [newSession, ...prev]);
            setCurrentSessionId(newSessionId);
            setMessages([]);
            return newSessionId;
        }
    };

    // 会话删除
    const handleSessionDelete = async (sessionId: string) => {
        try {
            await ChatApiService.deleteSession(sessionId);
            setSessions(prev => prev.filter(session => session.session_id !== sessionId));
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
                setMessages([]);
            }
        } catch (error) {
            console.error('删除会话失败:', error);
        }
    };

    // 会话归档
    const handleSessionArchive = async (sessionId: string) => {
        try {
            await ChatApiService.archiveSession(sessionId, true);
            setSessions(prev => prev.filter(session => session.session_id !== sessionId));
            fetchArchivedSessions();
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
                setMessages([]);
            }
        } catch (error) {
            console.error('归档会话失败:', error);
        }
    };

    // 会话恢复
    const handleSessionRestore = async (sessionId: string) => {
        try {
            await ChatApiService.archiveSession(sessionId, false);
            fetchArchivedSessions();
            const response = await ChatApiService.getSessions(user?.id);
            setSessions(response.sessions || []);
        } catch (error) {
            console.error('恢复会话失败:', error);
        }
    };

    // 删除知识库工具
    const handleToolRemove = async (toolId: string) => {
        try {
            await KnowledgeApiService.deleteDocument(toolId, user?.id);
            setRagTools(prev => prev.filter(tool => tool.id !== toolId));
        } catch (error) {
            console.error('删除知识库文档失败:', error);
        }
    };

    // 上传知识库文件
    const handleFileUpload = async (files: File[]) => {
        const file = files[0]; // 处理第一个文件
        if (!file) return;

        setKnowledgeUploadState({ isUploading: true, fileName: file.name, status: 'uploading' });
        try {
            const response = await KnowledgeApiService.uploadDocument(file, user?.id);
            if (response.success && response.documentId) {
                setRagTools(prev => [...prev, {
                    id: response.documentId,
                    name: file.name,
                    type: getFileType(file.name),
                    icon: getFileIcon(file.name),
                }]);
                setKnowledgeUploadState({ isUploading: false, fileName: file.name, status: 'success' });
            } else {
                setKnowledgeUploadState({ isUploading: false, fileName: file.name, status: 'error', error: response.error });
            }
        } catch (error) {
            setKnowledgeUploadState({ isUploading: false, fileName: file.name, status: 'error', error: 'Upload failed' });
        }
    };

    // ─── 云存储连接/断开 ──────────────────────

    // Cloud/Email/AI Index handlers 已由 hooks 管理 (useCloudStorage, useEmailProviders, useAiIndexWorker)

    const handleResourceSelect = (source: string, id: string, name: string, folderName: string) => {
        // Disabled click-to-mention. Drag-and-drop handles adding mentions to the chat.
    };

    /**
     * 按需构建本地文件树上下文：
     * - @[Local/file] → 只带该文件在树中的上下 2 行（含 AI 标签）
     * - "整理"/"所有文件" → 完整文件树
     * - 普通聊天 → 只提示已挂载，不带树
     */
    const buildDynamicFileTreeContext = (message: string, fullTree: string): string => {
        const treeLines = fullTree.split('\n');
        const totalItems = treeLines.filter(l => l.trim()).length;

        // 1. 检测 @[Local/...] 引用
        const localRefs = message.match(/@\[Local\/([^\]]+)\]/g);
        if (localRefs && localRefs.length > 0) {
            const relevantLineIndices = new Set<number>();
            for (const ref of localRefs) {
                const filePath = ref.slice(7, -1); // 去掉 @[Local/ 和 ]
                const fileName = filePath.split('/').pop() || filePath;
                for (let i = 0; i < treeLines.length; i++) {
                    if (treeLines[i].includes(fileName)) {
                        // 上下 2 行（含标签行 ↳）
                        for (let j = Math.max(0, i - 2); j <= Math.min(treeLines.length - 1, i + 2); j++) {
                            relevantLineIndices.add(j);
                        }
                    }
                }
            }
            if (relevantLineIndices.size > 0) {
                const sorted = Array.from(relevantLineIndices).sort((a, b) => a - b);
                const snippet = sorted.map(i => treeLines[i]).join('\n');
                return `[System Local Files Context]\nFiles near referenced items (${totalItems} total in folder):\n${snippet}\n\nUse 'read_local_file' tool to access any file by its exact path.`;
            }
        }

        // 2. 检测需要完整文件树的关键词
        const fullTreeKeywords = ['整理', '所有文件', '全部文件', '文件夹', '归类', '分类', '重命名', 'organize', 'all files', 'rename all', 'sort files'];
        if (fullTreeKeywords.some(kw => message.toLowerCase().includes(kw))) {
            return `[System Local Files Context]\nThe user has mounted a local folder. Here is the complete file tree:\n${fullTree}\n\nUse 'read_local_file' tool to read any file by its exact path.`;
        }

        // 3. 普通聊天 → 轻量提示
        return `[System Local Files Context]\nThe user has mounted a local folder with ${totalItems} items. Use 'read_local_file' tool to access files by path.`;
    };

    // ==================== HITL: 用户授权/拒绝本地文件操作 ====================

    // ─── Architecture 3.0 Phase 4: HITL 授权后执行（共享 Dispatcher） ───
    const handleToolApprove = async (messageId: string) => {
        const targetMsg = messages.find(m => m.message_id === messageId);
        if (!targetMsg?.toolRequest || targetMsg.toolRequest.status !== 'pending') return;

        const { tool, args } = targetMsg.toolRequest;

        // 1. 更新卡片状态为 approved
        setMessages(prev => prev.map(msg =>
            msg.message_id === messageId
                ? { ...msg, toolRequest: { ...msg.toolRequest!, status: 'approved' as const } }
                : msg
        ));
        setIsLoading(true);

        // 2. 通过 Dispatcher 统一执行
        let responseText = '';
        if (!localDirHandle) {
            responseText = '[Error: No local directory mounted]';
        } else {
            const ctx: FrontendToolContext = {
                localDirHandle,
                aiIndex,
                refreshFileTree: async () => {
                    const newTree = await scanDirectory(localDirHandle);
                    setLocalFileTreeListing(buildFileTreeListing(newTree, 0, aiIndex));
                },
                setMessageStatus: (status, content) => {
                    setMessages(prev => prev.map(msg =>
                        msg.message_id === messageId ? { ...msg, status: status as Message['status'], content } : msg
                    ));
                },
                apiBaseUrl: API_BASE_URL,
                authToken: localStorage.getItem('auth_token'),
            };
            responseText = await dispatchFrontendTool(tool, args, ctx);
        }

        // 3. 将结果作为下一轮发回给后端
        setTimeout(() => {
            handleSendMessage(`[System Tool Response]: ${responseText}`, undefined, undefined, undefined, true);
        }, 500);
    };

    const handleToolReject = (messageId: string) => {
        // 1. 更新卡片状态为 rejected
        setMessages(prev => prev.map(msg =>
            msg.message_id === messageId
                ? { ...msg, toolRequest: { ...msg.toolRequest!, status: 'rejected' as const } }
                : msg
        ));

        // 2. 通知后端用户拒绝
        const targetMsg = messages.find(m => m.message_id === messageId);
        const toolName = targetMsg?.toolRequest?.tool || 'unknown';
        setTimeout(() => {
            handleSendMessage(
                `[System Tool Response]: [User Denied] The user rejected the "${toolName}" operation. Do NOT retry this operation. Explain to the user that the operation was cancelled and ask how they would like to proceed instead.`,
                undefined, undefined, undefined, true
            );
        }, 500);
    };

    // 发送消息
    const handleSendMessage = async (content: string, pendingFiles?: File[], thinkingMode?: ThinkingMode, mentions?: MentionItem[], isToolResponse?: boolean, forcedSkillId?: string) => {
        const pendingFile = pendingFiles && pendingFiles.length > 0 ? pendingFiles[0] : undefined;

        // Use ref to avoid stale closure (critical for onToolRequest callbacks)
        let sessionId = currentSessionIdRef.current || currentSessionId;
        if (!sessionId) {
            sessionId = await handleNewSession();
        }
        if (!sessionId) return;

        const fileNamesDisplay = pendingFiles && pendingFiles.length > 0
            ? pendingFiles.map(f => `📎 ${f.name}`).join(', ')
            : '';

        const userMessage: Message = {
            message_id: uuidv4(),
            session_id: sessionId,
            role: 'user',
            content: fileNamesDisplay ? `[${fileNamesDisplay}] ${content}` : content,
            timestamp: Date.now(),
            is_final: true,
        };

        if (!isToolResponse) {
            setMessages(prev => [...prev, userMessage]);
        }
        setIsLoading(true);
        abortControllerRef.current = new AbortController();

        // 按需动态裁剪本地文件树上下文，而非每次倾倒整棵树
        let enhancedDocumentContext = uploadedFileContext?.extractedText || '';
        
        // --- 注入 Email IDs 面向 AI (不在聊天气泡中显示) ---
        if (mentions && mentions.length > 0) {
            const emailMentionsContext = mentions.map(m => `[Reference ID Map] Name: ${m.name}, Source: ${m.source}, ID: ${m.id}`).join('\n');
            enhancedDocumentContext = enhancedDocumentContext
                ? `${enhancedDocumentContext}\n\n${emailMentionsContext}`
                : emailMentionsContext;
        }

        if (localFileTreeListing) {
            const localTreeContext = buildDynamicFileTreeContext(content, localFileTreeListing);
            if (localTreeContext) {
                enhancedDocumentContext = enhancedDocumentContext
                    ? `${enhancedDocumentContext}\n\n${localTreeContext}`
                    : localTreeContext;
            }
        }

        const aiMessageId = uuidv4();
        const aiMessage: Message = {
            message_id: aiMessageId,
            session_id: sessionId,
            role: 'ai',
            content: '',
            timestamp: Date.now(),
            is_final: false,
        };
        setMessages(prev => [...prev, aiMessage]);

        try {
            // ─── Unified attachment handling: split media (native Gemini) vs docs (text extraction) ───
            let fileDocumentContext = enhancedDocumentContext;
            const nativeAttachments: Array<{ type: 'inline_data' | 'file_data'; mimeType: string; data?: string; fileUri?: string; name?: string }> = [];

            if (pendingFiles && pendingFiles.length > 0) {
                // Partition files into documents (text extraction) vs media (native multimodal)
                const documentFiles = pendingFiles.filter(f => !isNativeMediaFile(f));
                const mediaFiles = pendingFiles.filter(f => isNativeMediaFile(f));

                // ─── Process document files: officeparser text extraction → documentContext ───
                if (documentFiles.length > 0) {
                    if (documentFiles.length === 1) {
                        const pendingFile = documentFiles[0];
                        setFileUploadState({ file: pendingFile, progress: 0, status: 'uploading' });
                        try {
                            setFileUploadState(prev => prev ? { ...prev, progress: 30, status: 'uploading' } : null);
                            const parseResult = await FileApiService.parseDocument(pendingFile);
                            setFileUploadState(prev => prev ? { ...prev, progress: 60, status: 'parsing' } : null);

                            if (parseResult.success && parseResult.text) {
                                const extractedText = `\n=== File: ${pendingFile.name} ===\n${parseResult.text}`;
                                setUploadedFileContext({ extractedText, filename: pendingFile.name });
                                setFileUploadState(prev => prev ? { ...prev, extractedText, status: 'parsing' } : null);
                                fileDocumentContext = fileDocumentContext
                                    ? `${fileDocumentContext}\n\n${extractedText}`
                                    : extractedText;
                            } else {
                                throw new Error(parseResult.error || 'Failed to extract text from file');
                            }
                        } catch (parseError) {
                            const errorMsg = parseError instanceof Error ? parseError.message : 'File parsing failed';
                            setMessages(prev => prev.map(msg =>
                                msg.message_id === aiMessageId ? { ...msg, content: `❌ ${errorMsg}`, is_final: true } : msg
                            ));
                            setFileUploadState(prev => prev ? { ...prev, status: 'error', error: errorMsg } : null);
                            setIsLoading(false);
                            return;
                        }
                    } else {
                        // Multi-document handling
                        setMessages(prev => prev.map(msg =>
                            msg.message_id === aiMessageId
                                ? { ...msg, content: `🔄 Processing ${documentFiles.length} documents...` }
                                : msg
                        ));
                        const parseResults: { success: boolean; fileName: string; text?: string; error?: string }[] = [];
                        for (let i = 0; i < documentFiles.length; i++) {
                            try {
                                const result = await FileApiService.parseDocument(documentFiles[i]);
                                parseResults.push({ success: result.success, fileName: documentFiles[i].name, text: result.text });
                            } catch (e) {
                                parseResults.push({ success: false, fileName: documentFiles[i].name, error: e instanceof Error ? e.message : String(e) });
                            }
                            setMessages(prev => prev.map(msg =>
                                msg.message_id === aiMessageId
                                    ? { ...msg, content: `🔄 Processing documents... (${i + 1}/${documentFiles.length})` }
                                    : msg
                            ));
                        }
                        const successResults = parseResults.filter(r => r.success && r.text);
                        if (successResults.length === 0 && mediaFiles.length === 0) {
                            const errorMsg = parseResults.filter(r => !r.success).map(r => `${r.fileName}: ${r.error}`).join('\n');
                            setMessages(prev => prev.map(msg =>
                                msg.message_id === aiMessageId
                                    ? { ...msg, content: `❌ File processing failed:\n${errorMsg}`, is_final: true }
                                    : msg
                            ));
                            setIsLoading(false);
                            return;
                        }
                        if (successResults.length > 0) {
                            const combinedText = successResults.map(r => `\n=== File: ${r.fileName} ===\n${r.text}`).join('\n\n');
                            const combinedFilenames = successResults.map(r => r.fileName).join(', ');
                            setUploadedFileContext({ extractedText: combinedText, filename: combinedFilenames });
                            setMessages(prev => prev.map(msg =>
                                msg.message_id === aiMessageId ? { ...msg, content: '' } : msg
                            ));
                            fileDocumentContext = fileDocumentContext
                                ? `${fileDocumentContext}\n\n${combinedText}`
                                : combinedText;
                        }
                    }
                }

                // ─── Process media files: convert to base64 → native Gemini multimodal ───
                if (mediaFiles.length > 0) {
                    setMessages(prev => prev.map(msg =>
                        msg.message_id === aiMessageId
                            ? { ...msg, content: `🖼️ Preparing ${mediaFiles.length} media file(s) for AI analysis...` }
                            : msg
                    ));
                    for (const mediaFile of mediaFiles) {
                        try {
                            const base64Data = await fileToBase64(mediaFile);
                            nativeAttachments.push({
                                type: 'inline_data',
                                mimeType: mediaFile.type || 'application/octet-stream',
                                data: base64Data,
                                name: mediaFile.name,
                            });
                        } catch (err) {
                            console.error(`[AppLayout] Failed to read media file ${mediaFile.name}:`, err);
                        }
                    }
                    // Clear the placeholder message
                    setMessages(prev => prev.map(msg =>
                        msg.message_id === aiMessageId ? { ...msg, content: '' } : msg
                    ));
                }

                // Mark file upload as done before entering chat stream
                setFileUploadState(prev => prev ? { ...prev, status: 'done', progress: 100 } : null);

            } else if (uploadedFileContext) {
                // Follow-up questions about previously uploaded file — inject context
                fileDocumentContext = fileDocumentContext
                    ? `${fileDocumentContext}\n\n${uploadedFileContext.extractedText}`
                    : uploadedFileContext.extractedText;
            }

            // ─── ALL paths now flow through the unified main chat pipeline ───
            await ChatApiService.sendMessageStream(
                sessionId,
                content,
                (text) => {
                    setMessages(prev => prev.map(msg =>
                        msg.message_id === aiMessageId ? { ...msg, content: msg.content + text } : msg
                    ));
                },
                () => {
                    setMessages(prev => prev.map(msg =>
                        msg.message_id === aiMessageId ? { ...msg, is_final: true } : msg
                    ));
                    setIsLoading(false);
                    initializeApp();
                },
                (error) => {
                    setMessages(prev => prev.map(msg =>
                        msg.message_id === aiMessageId ? { ...msg, content: `Error: ${error}`, is_final: true } : msg
                    ));
                    setIsLoading(false);
                },
                {
                    useKnowledge: isKnowledgeSearchEnabled,
                    userId: user?.id,
                    thinkingMode: thinkingMode,
                    enabledDocumentIds: ragTools.filter(tool => tool.enabled !== false).map(tool => tool.id),
                    enabledCaseFolderIds: caseFolders.filter(f => f.enabled).map(f => f.id),
                    documentContext: fileDocumentContext ? fileDocumentContext : undefined,
                    attachments: nativeAttachments.length > 0 ? nativeAttachments : undefined,
                    signal: abortControllerRef.current?.signal,
                    forcedSkillId: forcedSkillId, // 🧩 Architecture 3.0: 绿色通道
                    onThinking: (thinkingText) => {
                        setMessages(prev => prev.map(msg =>
                            msg.message_id === aiMessageId
                                ? { ...msg, thinkingContent: (msg.thinkingContent || '') + thinkingText + '\n' }
                                : msg
                        ));
                    },
                    onCitations: (citations) => {
                        setMessages(prev => prev.map(msg =>
                            msg.message_id === aiMessageId ? { ...msg, citations: citations } : msg
                        ));
                    },
                    onStatus: (status) => {
                        setMessages(prev => prev.map(msg =>
                            msg.message_id === aiMessageId ? { ...msg, status: status } : msg
                        ));
                    },
                    onFileDownload: async (event) => {
                        console.log('[AppLayout] File download:', event.fileName, 'saveOptions:', event.saveOptions);
                        try {
                            // Base64 → Blob
                            const byteCharacters = atob(event.base64);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: event.mimeType });

                            // Show file card in chat message
                            const fileExt = event.fileName.split('.').pop()?.toLowerCase();
                            const fileIcon = fileExt === 'xlsx' ? '📊' : '📄';
                            const fileCard = `\n\n---\n${fileIcon} **${event.fileName}**`;
                            setMessages(prev => prev.map(msg =>
                                msg.message_id === aiMessageId
                                    ? { ...msg, content: (msg.content || '') + fileCard }
                                    : msg
                            ));

                            if (localDirHandle) {
                                // 保存到本地文件夹
                                try {
                                    const fileHandle = await localDirHandle.getFileHandle(event.fileName, { create: true });
                                    const writable = await fileHandle.createWritable();
                                    await writable.write(blob);
                                    await writable.close();
                                    console.log(`[AppLayout] File saved to local folder: ${event.fileName}`);
                                } catch (fsError) {
                                    console.warn('[AppLayout] Failed to save to local folder, falling back to download:', fsError);
                                    // 降级：触发浏览器下载
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = event.fileName;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    URL.revokeObjectURL(url);
                                }
                            } else {
                                // 无本地文件夹：触发浏览器下载
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = event.fileName;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                            }
                        } catch (error) {
                            console.error('[AppLayout] File download error:', error);
                        }
                    },
                    onWorkflowProgress: (event) => {
                        console.log('[AppLayout] Workflow progress:', event.dagName, event.status, event.progress);
                        const progressText = event.status === 'starting'
                            ? `⚙️ Starting workflow: ${event.dagName}...`
                            : event.status === 'completed'
                                ? `✅ Workflow "${event.dagName}" completed.`
                                : `⏳ ${event.dagName} — ${event.content || 'Processing...'}${event.progress ? ` (${event.progress}%)` : ''}`;
                        setMessages(prev => prev.map(msg =>
                            msg.message_id === aiMessageId
                                ? { ...msg, content: progressText, status: event.status === 'completed' ? 'answering' : 'searching' }
                                : msg
                        ));
                    },
                    onUIEvent: (event) => {
                        // __UI_EVENT__ 自动弹窗预览
                        console.log('[AppLayout] UI Event:', event.action, event.payload);
                        if (event.action === 'AUTO_PREVIEW' && event.payload) {
                            const { base64, type, fileName } = event.payload;
                            if (base64) {
                                // 将 base64 转为 Blob
                                try {
                                    const byteChars = atob(base64);
                                    const byteNums = new Array(byteChars.length);
                                    for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
                                    const blob = new Blob([new Uint8Array(byteNums)]);
                                    setPreviewFile({
                                        name: fileName || `document.${type || 'docx'}`,
                                        data: blob,
                                        source: 'AI Generated',
                                    });
                                } catch (err) {
                                    console.error('[AppLayout] Failed to decode base64 for preview:', err);
                                }
                            } else if (event.payload.url) {
                                // URL 方式
                                setPreviewFile({
                                    name: fileName || `document.${type || 'docx'}`,
                                    data: event.payload.url,
                                    source: 'AI Generated',
                                });
                            }
                        }
                    },
                    onTitleUpdate: (title, convId) => {
                        // 更新侧边栏会话标题
                        setSessions(prev => prev.map(session =>
                            session.session_id === convId ? { ...session, title } : session
                        ));
                    },
                    // ─── Architecture 3.0 Phase 4: onToolRequest → Dispatcher ───
                    onToolRequest: async (tool, args) => {
                        console.log('[AppLayout] Tool request:', tool, args);

                        if (!localDirHandle) {
                            setTimeout(() => {
                                handleSendMessage(`[System Tool Response]: [Error: No local directory mounted in the browser. Please mount a local folder first.]`, undefined, undefined, undefined, true);
                            }, 500);
                            return;
                        }

                        // HITL: 高危写操作需要用户授权
                        const WRITE_TOOLS = ['write_local_note', 'write_local_file', 'create_local_folder', 'move_local_files', 'create_folder_and_move', 'rename_local_file', 'delete_local_file', 'manage_workspace_files'];
                        if (WRITE_TOOLS.includes(tool)) {
                            setMessages(prev => prev.map(msg =>
                                msg.message_id === aiMessageId
                                    ? {
                                        ...msg,
                                        is_final: true,
                                        toolRequest: { tool, args, status: 'pending' as const },
                                    }
                                    : msg
                            ));
                            setIsLoading(false);
                            return;
                        }

                        // 通过 Dispatcher 统一执行
                        const ctx: FrontendToolContext = {
                            localDirHandle,
                            aiIndex,
                            refreshFileTree: async () => {
                                const newTree = await scanDirectory(localDirHandle);
                                setLocalFileTreeListing(buildFileTreeListing(newTree, 0, aiIndex));
                            },
                            setMessageStatus: (status, content) => {
                                setMessages(prev => prev.map(msg =>
                                    msg.message_id === aiMessageId ? { ...msg, status: status as Message['status'], content } : msg
                                ));
                            },
                            apiBaseUrl: API_BASE_URL,
                            authToken: localStorage.getItem('auth_token'),
                        };
                        const responseText = await dispatchFrontendTool(tool, args, ctx);

                        setTimeout(() => {
                            handleSendMessage(`[System Tool Response]: ${responseText}`, undefined, undefined, undefined, true);
                        }, 500);
                    }
                }
            );
        } catch (error) {
            console.error('发送消息失败:', error);
            setMessages(prev => prev.map(msg =>
                msg.message_id === aiMessageId
                    ? { ...msg, content: 'Sorry, could not connect to server. Please check if the backend is running.', is_final: true }
                    : msg
            ));
            setFileUploadState(prev => prev ? { ...prev, status: 'error', error: 'Connection failed' } : null);
            setIsLoading(false);
        }
    };

    // RAG Panel component helper
    const renderRagPanel = () => (
        <RagToolsPanel
            ragTools={ragTools}
            onToolRemove={handleToolRemove}
            onAddTool={() => { }}
            onFileUpload={handleFileUpload}
            currentLanguage={currentLanguage}
            isKnowledgeSearchEnabled={isKnowledgeSearchEnabled}
            onKnowledgeSearchToggle={setIsKnowledgeSearchEnabled}
            onClose={() => setRagPanelVisible(false)}
            uploadState={knowledgeUploadState}
            isLoading={isRagToolsLoading}
            onLibraryOpen={() => navigate('/app/library')}
            caseFolders={caseFolders}
            onCaseFoldersChange={setCaseFolders}
            aiIndex={aiIndex}
            onLocalDirHandleChange={setLocalDirHandle} // Sync dir handle up to AppLayout
            onLocalFileTreeChange={setLocalFileTreeListing} // Sync file tree string to use as AI context
            onLocalFileSelect={(fileContent: string, fileName: string) => {
                // Create a File blob from the local file content and send it as a message attachment
                const blob = new Blob([fileContent], { type: 'text/plain' });
                const file = new File([blob], fileName, { type: 'text/plain' });
                handleSendMessage(`请分析这个本地文件的内容: ${fileName}`, [file]);
            }}
            cloudProviders={cloudProviders}
            cloudFiles={cloudFiles}
            onCloudConnect={handleCloudConnect}
            onCloudDisconnect={handleCloudDisconnect}
            onCloudRefresh={loadCloudFiles}
            onCloudFolderClick={handleCloudFolderClick}
            onCloudFolderBack={handleCloudFolderBack}
            onResourceSelect={handleResourceSelect}
            emailProviders={emailProviders}
            emailMessages={emailMessages}
            onEmailConnect={handleEmailConnect}
            onEmailDisconnect={handleEmailDisconnect}
            onEmailRefresh={loadEmailMessages}
            onEmailLoadMore={loadMoreEmails}
            onEmailRightClick={handleEmailRightClick}
            onFilePreview={(file) => setPreviewFile(file)}
            onAiAnalyze={async (fileTreeListing) => {
                try {
                    const result = await FileApiService.analyzeFileTree({
                        fileTree: fileTreeListing,
                        language: currentLanguage,
                    });
                    if (result.success && result.synthesis) {
                        handleSendMessage(
                            `📁 **File Analysis**\n\n${result.synthesis}`,
                            []
                        );
                    }
                } catch (err) {
                    console.error('[FileAnalysis] Error:', err);
                }
            }}
        />
    );

    // Handle logout with redirect
    const handleLogout = async () => {
        await logout();
        navigate('/', { replace: true });
    };

    return (
        <div className="h-screen flex bg-chat-bg">
            {/* 左侧边栏 */}
            <Sidebar
                sessions={sessions}
                currentSessionId={currentSessionId}
                onSessionSelect={handleSessionSelect}
                onNewSession={handleNewSession}
                onSessionDelete={handleSessionDelete}
                onSessionArchive={handleSessionArchive}
                onLanguageSelect={openLanguageSelector}
                currentLanguage={currentLanguage}
                onLibraryOpen={() => navigate('/app/library')}
                onLegalDrafterOpen={() => {
                    if (!isPaidUser()) {
                        showUpgradePrompt(currentLanguage.startsWith('zh') ? 'AI 起草' : 'AI Draft');
                        return;
                    }
                    setInitialDraftSessionId(null);
                    setForceNewDraft(prev => prev + 1);
                    navigate('/app/draft');
                }}
                archivedSessions={archivedSessions}
                onSessionRestore={handleSessionRestore}
                toolHistoryRecords={toolHistoryRecords}
                onToolHistorySelect={handleToolHistorySelect}
                onToolHistoryDelete={handleToolHistoryDelete}
                onLogout={handleLogout}
                onPricingOpen={openPricing}
                onSettingsOpen={() => navigate('/app/settings')}
                onGuideOpen={() => {
                    resetOnboarding();
                    openOnboarding();
                }}
                onStudioOpen={() => navigate('/app/studio')}
                user={user}
            />

            {/* 中央聊天区域 */}
            <div className="flex-1 flex flex-col relative">
                <ChatArea
                    messages={messages}
                    onSendMessage={handleSendMessage}
                    isLoading={isLoading}
                    currentSessionId={currentSessionId}
                    currentLanguage={currentLanguage}
                    fileUploadState={fileUploadState}
                    onLegalDrafterOpen={() => navigate('/app/draft')}
                    onCancelRequest={() => {
                        if (abortControllerRef.current) {
                            abortControllerRef.current.abort();
                            abortControllerRef.current = null;
                        }
                        setMessages(prev => prev.map((msg, index) =>
                            index === prev.length - 1 && msg.role === 'ai' && !msg.is_final
                                ? { ...msg, is_final: true }
                                : msg
                        ));
                        setIsLoading(false);
                    }}
                    inputMessage={chatInputMessage}
                    onInputMessageChange={setChatInputMessage}
                    mentionFiles={mentionFiles}
                    onToolApprove={handleToolApprove}
                    onToolReject={handleToolReject}
                    installedSkills={installedSkills}
                />

                {/* 右侧面板切换按钮 */}
                <button
                    onClick={toggleRagPanel}
                    className="absolute top-1/2 right-0 transform -translate-y-1/2 z-10"
                    title={isRagPanelVisible ? t('hideResources') : t('showResources')}
                    style={{
                        width: '24px',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #374151, #1f2937)',
                        border: 'none',
                        borderRadius: isRagPanelVisible ? '8px 0 0 8px' : '8px',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 6px rgba(31,41,55,0.35)',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #1f2937, #111827)';
                        e.currentTarget.style.boxShadow = '0 3px 12px rgba(17,24,39,0.45)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #374151, #1f2937)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(31,41,55,0.35)';
                    }}
                >
                    <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{
                            transition: 'transform 0.3s ease',
                            transform: isRagPanelVisible ? 'rotate(0deg)' : 'rotate(180deg)',
                        }}
                    >
                        <polyline points="9 18 15 12 9 6" />
                    </svg>
                </button>
            </div>

            {/* 右侧RAG工具面板 */}
            {isRagPanelVisible && (
                <div className="w-80 flex-shrink-0 bg-white/95 backdrop-blur-xl border-l border-gray-200/80 shadow-lg">
                    {renderRagPanel()}
                </div>
            )}

            {/* AI 法律文书起草 */}
            <LegalDrafter
                isOpen={isOnDraftRoute}
                onClose={() => {
                    setInitialDraftSessionId(null);
                    navigate('/app');
                }}
                initialSessionId={initialDraftSessionId}
                forceNewDraft={forceNewDraft}
                ragPanelComponent={renderRagPanel()}
                isRagPanelVisible={isRagPanelVisible}
                onRagPanelToggle={toggleRagPanel}
                onHistorySave={createToolHistorySave('draft')}
                currentLanguage={currentLanguage}
                localDirHandle={localDirHandle}
                localFileTreeListing={localFileTreeListing}
                caseFolders={caseFolders}
                mentionFiles={mentionFiles}
            />

            {/* 资料库 */}
            {isOnLibraryRoute && (
                <div className="fixed inset-0 z-50 bg-white">
                    <LibraryPage
                        onClose={() => navigate('/app')}
                        currentLanguage={currentLanguage}
                        cases={caseFolders}
                        onCasesChange={setCaseFolders}
                    />
                </div>
            )}

            {/* 🧩 Architecture 3.0: Creator Studio 应用大厅 */}
            {isOnStudioRoute && (
                <CreatorStudio
                    isOpen={true}
                    onClose={() => navigate('/app')}
                    currentLanguage={currentLanguage}
                    installedSkills={installedSkills}
                    onInstallChange={() => {
                        WorkflowApiService.getInstalled()
                            .then(response => {
                                if (response.items) setInstalledSkills(response.items);
                            })
                            .catch(err => console.warn('[AppLayout] Skills refresh failed:', err));
                    }}
                />
            )}

            {/* Architecture 3.0 Phase 4: 全局模态框统一插槽 */}
            <GlobalModalSlot
                currentLanguage={currentLanguage}
                onLanguageChange={onLanguageChange}
                isOnSettingsRoute={isOnSettingsRoute}
                onSettingsClose={() => { if (isOnSettingsRoute) navigate('/app'); }}
                user={user}
                readingEmail={readingEmail}
                setReadingEmail={setReadingEmail}
            />
        </div>
    );
};

export default AppLayout;
